import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  NoSymbolIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';

import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  TabularDataViewer,
} from '../../components/ui';
import { cn } from '../../shared/lib/cn';
import { approvalGates as fallbackApprovalGates } from './data';
import {
  fallbackAgentReview,
  fallbackWorkbook,
  useGateDecisions,
  useJudgmentalAccrualsWorkbook,
  useRefreshAgentReview,
  useSaveGateDecisions,
} from './api';
import type { ApprovalGate, AuditEntry, GateStatus, JournalLine } from './types';
import {
  buildNetsuiteCsv,
  downloadText,
  formatCurrency,
  statusLabel,
} from './utils';

const STATUS_CLASSES: Record<GateStatus, string> = {
  pending: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-dark-surface-2 dark:text-gray-300 dark:border-dark-border',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
  hold: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800',
};

const initialStatuses = Object.fromEntries(
  fallbackApprovalGates.map(gate => [gate.id, 'pending' as GateStatus]),
);

export default function WorkpaperPage() {
  const { data: workbook, error: workbookError, isError, isLoading } = useJudgmentalAccrualsWorkbook();
  const workbookForState = workbook ?? fallbackWorkbook;
  const [statuses, setStatuses] = useState<Record<string, GateStatus>>(initialStatuses);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [agentReview, setAgentReview] = useState(fallbackAgentReview);
  const { data: savedDecisions } = useGateDecisions();
  const saveDecisions = useSaveGateDecisions();
  const refreshAgentReview = useRefreshAgentReview();

  const approvalGates = workbookForState.approvalGates;
  const legalMatters = workbookForState.legalMatters;
  const probableMatters = legalMatters.filter(matter => matter.treatment === 'accrue');
  const disclosureMatters = legalMatters.filter(matter => matter.treatment === 'disclose');
  const impactLines = workbookForState.impactLines;
  const journalEntries = workbookForState.journalEntries;

  const approvedCount = useMemo(
    () => Object.values(statuses).filter(status => status === 'approved').length,
    [statuses],
  );
  const allApproved = approvedCount === approvalGates.length;
  const csvText = useMemo(
    () => buildNetsuiteCsv(statuses, journalEntries, workbookForState.sourceAgent),
    [statuses, journalEntries, workbookForState.sourceAgent],
  );
  const totalAccrual = probableMatters.reduce((sum, matter) => sum + matter.recommendedAccrual, 0);
  const netPlImpact = impactLines
    .filter(line => line.statement === 'P&L')
    .reduce((sum, line) => sum + line.amount, 0);

  useEffect(() => {
    if (!savedDecisions) return;
    if (Object.keys(savedDecisions.statuses).length > 0) {
      setStatuses(current => ({ ...current, ...savedDecisions.statuses }));
    }
    if (savedDecisions.auditLog.length > 0) {
      setAuditLog(savedDecisions.auditLog as unknown as AuditEntry[]);
    }
  }, [savedDecisions]);

  function addAudit(action: string, item: string, approver: string, reason?: string) {
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    const nextAuditLog = [
      ...auditLog,
      {
        id: `${Date.now()}-${auditLog.length}`,
        timestamp,
        action,
        item,
        approver,
        reason,
      },
    ];
    setAuditLog(nextAuditLog);
    if (process.env.NODE_ENV !== 'test') {
      saveDecisions.mutate({ statuses, auditLog: nextAuditLog as unknown as Record<string, unknown>[] });
    }
  }

  function updateGate(gate: ApprovalGate, status: GateStatus, reason?: string) {
    const timestamp = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    const nextEntry: AuditEntry = {
      id: `${Date.now()}-${auditLog.length}`,
      timestamp,
      action: status.toUpperCase(),
      item: gate.title,
      approver: status === 'approved' ? gate.approver : 'User',
      reason,
    };
    const nextStatuses = { ...statuses, [gate.id]: status };
    const nextAuditLog = [...auditLog, nextEntry];
    setStatuses(nextStatuses);
    setAuditLog(nextAuditLog);
    if (process.env.NODE_ENV !== 'test') {
      saveDecisions.mutate({ statuses: nextStatuses, auditLog: nextAuditLog as unknown as Record<string, unknown>[] });
    }
  }

  function gateStatus(gateId: string): GateStatus {
    return statuses[gateId] ?? 'pending';
  }

  function rejectGate(gate: ApprovalGate) {
    const reason = window.prompt('Reason for rejection');
    if (!reason) return;
    updateGate(gate, 'rejected', reason);
  }

  function exportCsv() {
    if (!allApproved) {
      const shouldContinue = window.confirm(
        `${approvalGates.length - approvedCount} gate(s) are not approved. Export CSV for review anyway? Do not post until all gates are approved.`,
      );
      if (!shouldContinue) return;
    }
    downloadText('EC02e_JE_NetSuite_Apr2025.csv', csvText, 'text/csv');
    addAudit('CSV EXPORTED', 'NetSuite JE import file', 'User');
  }

  async function copyCsv() {
    try {
      await navigator.clipboard.writeText(csvText);
      setCopyMessage('CSV copied');
    } catch {
      setCopyMessage('Clipboard unavailable');
    }
  }

  function runAgentReview() {
    refreshAgentReview.mutate(
      { statuses, focus: 'approval readiness and NetSuite posting blockers' },
      { onSuccess: setAgentReview },
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1760px] px-4 py-5 sm:px-6 lg:px-8">
        <Panel>
          <PanelBody>
            <p className="text-sm text-gray-600 dark:text-gray-300">Loading Judgmental Accruals workbook from Redshift...</p>
          </PanelBody>
        </Panel>
      </div>
    );
  }

  if (isError || !workbook) {
    return (
      <div className="mx-auto max-w-[1760px] px-4 py-5 sm:px-6 lg:px-8">
        <Panel>
          <PanelHeader>
            <PanelTitle>Redshift Workbook Not Connected</PanelTitle>
          </PanelHeader>
          <PanelBody className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Judgmental Accruals requires a configured Redshift connector. Runtime mock workbook data is disabled.
            </p>
            {workbookError instanceof Error && (
              <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                {workbookError.message}
              </p>
            )}
          </PanelBody>
        </Panel>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1760px] px-4 py-5 sm:px-6 lg:px-8">
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px] xl:items-start">
        <div className="space-y-6">
          <section className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
            <Panel className="overflow-hidden">
              <PanelHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <PanelTitle>Legal Contingency Review</PanelTitle>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    ASC 450 classification and treatment for {workbook.periodLabel}.
                  </p>
                </div>
                <Badge variant="danger">{formatCurrency(totalAccrual)} accrual</Badge>
              </PanelHeader>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                    <tr>
                      <th className="px-5 py-3 font-medium">Matter</th>
                      <th className="px-5 py-3 font-medium">Counterparty</th>
                      <th className="px-5 py-3 font-medium">Classification</th>
                      <th className="px-5 py-3 text-right font-medium">Range</th>
                      <th className="px-5 py-3 text-right font-medium">GC Rec.</th>
                      <th className="px-5 py-3 font-medium">Treatment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                    {legalMatters.map(matter => (
                      <tr key={matter.id} className="bg-white dark:bg-dark-surface">
                        <td className="px-5 py-4 align-top">
                          <div className="font-semibold text-charcoal dark:text-gray-100">{matter.id}</div>
                          <div className="mt-1 max-w-sm text-gray-500 dark:text-gray-400">{matter.description}</div>
                        </td>
                        <td className="px-5 py-4 align-top text-gray-600 dark:text-gray-300">{matter.counterparty}</td>
                        <td className="px-5 py-4 align-top">
                          <StatusBadge status={matter.treatment === 'accrue' ? 'approved' : 'hold'}>
                            {matter.probability}
                          </StatusBadge>
                        </td>
                        <td className="px-5 py-4 text-right align-top tabular-nums text-gray-600 dark:text-gray-300">
                          {formatCurrency(matter.lowEstimate)} to {formatCurrency(matter.highEstimate)}
                        </td>
                        <td className="px-5 py-4 text-right align-top font-semibold tabular-nums text-charcoal dark:text-gray-100">
                          {formatCurrency(matter.recommendedAccrual)}
                        </td>
                        <td className="px-5 py-4 align-top">
                          {matter.treatment === 'accrue' ? 'Accrue' : 'Disclose only'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Control Position</PanelTitle>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No posting without gate evidence.
                </p>
              </PanelHeader>
              <PanelBody className="space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <Metric label="Probable" value={String(probableMatters.length)} />
                  <Metric label="Disclose" value={String(disclosureMatters.length)} />
                  <Metric label="Net P&L" value={formatCurrency(netPlImpact, { accounting: true })} tone="danger" />
                  <Metric label="Journal date" value={workbook.journalDate} />
                </div>
                <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
                  EC-02e is the final accrual workflow before cut-off. Prior month assessments do not carry forward automatically.
                </div>
              </PanelBody>
            </Panel>
          </section>

          <Panel>
            <PanelHeader>
              <PanelTitle>Journal Entries</PanelTitle>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                NetSuite-ready lines with accounting basis and gate status.
              </p>
            </PanelHeader>
            <PanelBody className="space-y-4">
              {journalEntries.map(entry => (
                <div key={entry.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-charcoal dark:text-gray-100">
                        {entry.id} - {entry.title}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {entry.standard} - {entry.basis}
                      </div>
                    </div>
                    <StatusBadge status={gateStatus(entry.gateId)}>{statusLabel(gateStatus(entry.gateId))}</StatusBadge>
                  </div>
                  <div className="mt-4 grid gap-2">
                    {entry.lines.map(line => (
                      <JournalLineRow key={line.externalId} line={line} status={gateStatus(line.gateId)} />
                    ))}
                  </div>
                </div>
              ))}
            </PanelBody>
          </Panel>

          <section className="grid gap-5 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
            <Panel>
              <PanelHeader>
                <PanelTitle>Footnote Disclosures</PanelTitle>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Reasonably possible matters without JE impact.</p>
              </PanelHeader>
              <PanelBody className="space-y-4">
                {disclosureMatters.map(matter => (
                  <div key={matter.id} className="border-l-2 border-amber-300 pl-4 dark:border-amber-700">
                    <div className="text-sm font-semibold text-charcoal dark:text-gray-100">{matter.id}</div>
                    <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
                      {matter.description}. Estimated range: {formatCurrency(matter.lowEstimate)} to {formatCurrency(matter.highEstimate)}. No accrual recorded under ASC 450-20-50.
                    </p>
                  </div>
                ))}
              </PanelBody>
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Financial Statement Impact</PanelTitle>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">P&L and OCI view for reviewer sign-off.</p>
              </PanelHeader>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                    <tr>
                      <th className="px-5 py-3 text-left font-medium">Line</th>
                      <th className="px-5 py-3 text-left font-medium">Account</th>
                      <th className="px-5 py-3 text-right font-medium">Amount</th>
                      <th className="px-5 py-3 text-left font-medium">Statement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                    {impactLines.map(line => (
                      <tr key={`${line.account}-${line.label}`}>
                        <td className="px-5 py-3">
                          <div className="font-medium text-charcoal dark:text-gray-100">{line.label}</div>
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{line.driver}</div>
                        </td>
                        <td className="px-5 py-3 text-gray-600 dark:text-gray-300">{line.account}</td>
                        <td className={cn('px-5 py-3 text-right font-semibold tabular-nums', line.amount < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400')}>
                          {formatCurrency(line.amount, { accounting: true })}
                        </td>
                        <td className="px-5 py-3 text-gray-600 dark:text-gray-300">{line.statement}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </section>
        </div>

        <aside className="space-y-5 xl:sticky xl:top-5">
          <Panel>
            <PanelHeader>
              <PanelTitle>Agent Review</PanelTitle>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Langfuse-traced EC-02e review for {workbook.periodLabel}.
              </p>
            </PanelHeader>
            <PanelBody className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={agentReview.riskLevel === 'high' ? 'danger' : agentReview.riskLevel === 'medium' ? 'warning' : 'success'}>
                  {agentReview.riskLevel} risk
                </Badge>
                <Badge variant="outline">{agentReview.usedLlm ? 'LLM traced' : 'Rules fallback'}</Badge>
                <Badge variant="secondary">{workbook.sourceMode}</Badge>
              </div>
              <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">{agentReview.summary}</p>
              <div className="space-y-2">
                {agentReview.findings.slice(0, 3).map(finding => (
                  <div key={finding} className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
                    {finding}
                  </div>
                ))}
              </div>
              {agentReview.langfuseTraceId && (
                <div className="break-all text-xs text-gray-500 dark:text-gray-400">
                  Langfuse trace: <span className="font-mono">{agentReview.langfuseTraceId}</span>
                </div>
              )}
              <Button size="sm" variant="secondary" onClick={runAgentReview} disabled={refreshAgentReview.isPending}>
                {refreshAgentReview.isPending ? 'Reviewing...' : 'Run agent review'}
              </Button>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader className="flex items-start justify-between gap-4">
              <div>
                <PanelTitle>Approval Gates</PanelTitle>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {approvedCount} of {approvalGates.length} approved.
                </p>
              </div>
              <Badge variant={allApproved ? 'success' : 'warning'}>
                {allApproved ? 'Ready' : 'Blocked'}
              </Badge>
            </PanelHeader>
            <PanelBody className="space-y-3">
              {approvalGates.map(gate => (
                <div key={gate.id} className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={gate.type === 'HITL' ? 'danger' : 'info'}>{gate.type}</Badge>
                        <StatusBadge status={gateStatus(gate.id)}>{statusLabel(gateStatus(gate.id))}</StatusBadge>
                      </div>
                      <div className="mt-2 text-sm font-semibold text-charcoal dark:text-gray-100">
                        {gate.title}
                      </div>
                      <div className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                        {gate.subtitle}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => updateGate(gate, 'approved')}>
                      <CheckIcon className="h-4 w-4" />
                      {gate.approveLabel}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateGate(gate, 'hold')}>
                      <PauseIcon className="h-4 w-4" />
                      Hold
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => rejectGate(gate)}>
                      <NoSymbolIcon className="h-4 w-4" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>NetSuite Export</PanelTitle>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Source agent: {workbook.sourceAgent}
              </p>
            </PanelHeader>
            <PanelBody className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <Button size="sm" variant="secondary" onClick={() => setCsvPreviewOpen(true)}>
                  <EyeIcon className="h-4 w-4" />
                  Preview
                </Button>
                <Button size="sm" variant="secondary" onClick={copyCsv}>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy
                </Button>
                <Button size="sm" variant={allApproved ? 'brand' : 'outline'} onClick={exportCsv}>
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  CSV
                </Button>
              </div>
              {copyMessage && (
                <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
                  {copyMessage}
                </div>
              )}
              <TabularDataViewer
                data={{
                  columns: ['External ID', 'Account', 'Debit', 'Credit', 'Status'],
                  rows: journalEntries.flatMap(entry => entry.lines).map(line => [
                    line.externalId,
                    line.accountNumber,
                    line.debit ? formatCurrency(line.debit) : '',
                    line.credit ? formatCurrency(line.credit) : '',
                    statusLabel(gateStatus(line.gateId)),
                  ]),
                }}
                filename="EC02e_JE_NetSuite_Apr2025_preview.csv"
                maxHeight="360px"
                dense
                searchable={false}
                exportable={false}
              />
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Audit Log</PanelTitle>
            </PanelHeader>
            <PanelBody>
              {auditLog.length === 0 ? (
                <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                  No decisions recorded.
                </div>
              ) : (
                <div className="max-h-[280px] space-y-3 overflow-auto">
                  {auditLog.map(entry => (
                    <div key={entry.id} className="border-l-2 border-gray-200 pl-3 text-sm dark:border-dark-border">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-charcoal dark:text-gray-100">{entry.action}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">{entry.timestamp}</span>
                      </div>
                      <div className="mt-1 text-gray-600 dark:text-gray-300">{entry.item}</div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">By: {entry.approver}</div>
                      {entry.reason && <div className="mt-1 text-xs text-rose-600 dark:text-rose-400">Reason: {entry.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
            </PanelBody>
          </Panel>
        </aside>
      </div>

      <Modal open={csvPreviewOpen} onClose={() => setCsvPreviewOpen(false)}>
        <ModalContent size="full">
          <ModalHeader>
            <ModalTitle>NetSuite CSV Preview</ModalTitle>
            <ModalDescription>Live gate state is embedded in the HITL Status column.</ModalDescription>
          </ModalHeader>
          <ModalBody>
            <pre className="max-h-[520px] overflow-auto rounded-lg bg-gray-950 p-4 text-xs leading-5 text-gray-100">
              {csvText}
            </pre>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setCsvPreviewOpen(false)}>Close</Button>
            <Button variant="brand" onClick={exportCsv}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download CSV
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}

function Metric({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'danger' }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase text-gray-500 dark:text-gray-400">{label}</div>
      <div className={cn('mt-1 text-lg font-semibold text-charcoal dark:text-gray-100', tone === 'danger' && 'text-rose-600 dark:text-rose-400')}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status, children }: { status: GateStatus; children: React.ReactNode }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', STATUS_CLASSES[status])}>
      {children}
    </span>
  );
}

function JournalLineRow({ line, status }: { line: JournalLine; status: GateStatus }) {
  return (
    <div className="grid gap-2 rounded-md bg-white px-3 py-2 text-sm dark:bg-dark-surface sm:grid-cols-[minmax(0,1fr)_120px_120px_92px] sm:items-center">
      <div className="min-w-0">
        <div className="font-medium text-charcoal dark:text-gray-100">
          {line.accountNumber} - {line.accountName}
        </div>
        <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{line.memo}</div>
      </div>
      <div className="text-right tabular-nums text-gray-600 dark:text-gray-300">
        {line.debit ? `DR ${formatCurrency(line.debit)}` : ''}
      </div>
      <div className="text-right tabular-nums text-gray-600 dark:text-gray-300">
        {line.credit ? `CR ${formatCurrency(line.credit)}` : ''}
      </div>
      <div className="sm:text-right">
        <StatusBadge status={status}>{statusLabel(status)}</StatusBadge>
      </div>
    </div>
  );
}
